import type { Json } from '../../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

/**
 * Payload row shape passed to the `import_deals_csv` RPC. Mirrors
 * {@link CsvDealRow} from `src/lib/admin-deal-csv-schemas.ts` so the same
 * object can travel client-side validation → RPC payload without conversion.
 * Optional fields are kept as `null` (rather than omitted) to keep the JSON
 * payload uniform — the RPC uses `NULLIF(v_row->>'col', '')` so nulls
 * round-trip cleanly.
 */
export type AdminDealCsvImportRow = {
  type: 'ready' | 'off_plan';
  zone: string;
  community: string;
  building: string | null;
  bedrooms: number;
  area_sqft: number;
  bathrooms: number | null;
  floor: number | null;
  handover: string | null;
  title: string;
  description: string;
  asking_price: number;
  original_price: number | null;
  amount_paid: number | null;
};

export type AdminDealCsvImportResult = {
  inserted_count: number;
  reference_ids: string[];
};

/**
 * Import a batch of CSV-validated deal rows. Routes through the
 * `import_deals_csv` RPC (SECURITY INVOKER) which:
 *   - enforces ≤500 rows (54000), non-empty payload (22023), auth.uid() set
 *     (42501) before touching the table.
 *   - assigns a provisional slug 'draft-{8 hex}' per row.
 *   - forces created_by := auth.uid() (front cannot spoof author).
 *   - inserts every row in a single PL/pgSQL function = single implicit
 *     Postgres transaction → UB12 all-or-nothing atomicity.
 *   - logs ONE audit_log row per batch (`deals_imported_csv` with
 *     inserted_count + reference_ids[]).
 *
 * RLS `deals_insert` gates the caller — admin / super_admin only. A buyer
 * caller surfaces as 42501. A CHECK violation (e.g. off_plan missing
 * original_price) surfaces as 23514 and rolls the whole batch back.
 *
 * Errors are re-thrown unchanged — the UI maps `error.code` (42501,
 * 54000, 22023, 23514) to a localised banner.
 */
export async function importDealsCsv(
  rows: AdminDealCsvImportRow[],
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminDealCsvImportResult> {
  const { data, error } = await client.rpc('import_deals_csv', {
    p_rows: rows as unknown as Json,
  });
  if (error) throw error;
  return data as unknown as AdminDealCsvImportResult;
}
